import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { GET_CONTACTS } from '../../../lib/graphql/queries/contacts';
import { GET_FOLLOW_UP_DECISIONS } from '../../../lib/graphql/queries/organizations';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import type { FollowUpStrategySegment } from '../../../api/nextFollowUp';

const PAGE_SIZE = 25;

export interface GQLFollowUpDecision {
  organizationId: string;
  contactId: string | null;
  decisionId: string;
  decisionType: string | null;
  decisionReason: string | null;
  confidenceScore: number | null;
  decidedAt: string | null;
  scheduledFor: string | null;
  executedAt: string | null;
  completedAt: string | null;
  flowExecutionId: string | null;
  cadenceStep: number | null;
  strategyId: string | null;
  reasoning: string | null;
  outcome: string | null;
  outcomeTimestamp: string | null;
  outcomeNotes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  contactFirstName: string | null;
  contactLastName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
}

interface FollowUpDecisionData {
  followUpDecisions: {
    decisions: GQLFollowUpDecision[];
    total: number;
    hasMore: boolean;
  } | null;
}

export interface GQLFlywheelContact {
  contactId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  recentOutcome: string | null;
  status: string | null;
  lifecycleStage: string | null;
  flowExecutions: { flowName: string | null }[] | null;
  allFlowExecutions: { flowName: string | null }[] | null;
}

interface FlywheelContactsData {
  contacts: {
    contacts: GQLFlywheelContact[];
    total: number;
    hasMore: boolean;
  } | null;
}

interface UseFlywheelFollowUpsOptions {
  enabled: boolean;
  decisionType?: string;
  strategySegment?: FollowUpStrategySegment;
}

export function useFlywheelFollowUpsGQL({
  enabled,
  decisionType,
  strategySegment,
}: UseFlywheelFollowUpsOptions) {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error, refetch } = useQuery<FollowUpDecisionData>(
    GET_FOLLOW_UP_DECISIONS,
    {
      variables: {
        organizationId: organization?.id ?? '',
        limit: PAGE_SIZE,
        offset: currentPage * PAGE_SIZE,
        decisionType: decisionType ?? null,
        strategySegment: strategySegment ?? null,
        pendingOnly: true,
      },
      skip: !enabled || !organization?.id,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );

  const total = data?.followUpDecisions?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    decisions: data?.followUpDecisions?.decisions ?? [],
    total,
    hasMore: data?.followUpDecisions?.hasMore ?? false,
    loading,
    error,
    currentPage,
    totalPages,
    pageSize: PAGE_SIZE,
    goToPage,
    resetPage,
    refetch,
  };
}

export function useFlywheelContactsGQL(enabled: boolean) {
  const { organization } = useOrganizationStore();
  const [currentPage, setCurrentPage] = useState(0);

  const { data, loading, error, refetch } = useQuery<FlywheelContactsData>(GET_CONTACTS, {
    variables: {
      organizationId: organization?.id ?? '',
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      sortBy: 'updatedAt',
      sortOrder: 'DESC',
      flywheelOptOut: false,
    },
    skip: !enabled || !organization?.id,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const total = data?.contacts?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const goToPage = useCallback((page: number) => setCurrentPage(Math.max(0, page)), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);

  return {
    contacts: data?.contacts?.contacts ?? [],
    total,
    hasMore: data?.contacts?.hasMore ?? false,
    loading,
    error,
    currentPage,
    totalPages,
    pageSize: PAGE_SIZE,
    goToPage,
    resetPage,
    refetch,
  };
}
